import React, { createContext, useContext, useState, useEffect } from 'react';
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { theme, darkTheme } from '../theme';

type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextValue {
  mode: ThemeMode;
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
  currentTheme: Theme;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Check if user has saved theme preference
  const savedTheme = localStorage.getItem('themeMode') as ThemeMode | null;
  // Check system preference if no saved theme
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  const [mode, setMode] = useState<ThemeMode>(
    savedTheme || (prefersDarkMode ? 'dark' : 'light')
  );

  // Initial setup of dark class on mount
  useEffect(() => {
    const initialMode = savedTheme || (prefersDarkMode ? 'dark' : 'light');
    if (initialMode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [savedTheme, prefersDarkMode]); // Run only on mount and when these values change

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    // Toggle dark class on document root for Tailwind dark mode
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  const handleSetMode = (newMode: ThemeMode) => {
    if (newMode === 'auto') {
      localStorage.removeItem('themeMode');
      setMode(prefersDarkMode ? 'dark' : 'light');
    } else {
      setMode(newMode);
    }
  };

  const currentTheme = mode === 'dark' ? darkTheme : theme;

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode: handleSetMode, currentTheme }}>
      <MuiThemeProvider theme={currentTheme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;

